// src/pages/Home.jsx
import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import TermsIconAdvanced from "../components/TermsIconAdvanced";

import logo from "../assets/logo.png";
import floralCorner from "../assets/65102635_9567363.png";

import venue1 from "../assets/35 inside pictures of the City Palace-Home to Maharaja Padmanabh Singh & the erstwhile royal family of Jaipur.webp";
import venue2 from "../assets/venue.jpg";
import venue3 from "../assets/Banquet-wedding.png";
import venue4 from "../assets/book-bouquet-hotel-in-udaipur-5-960x720.jpg";

import "../styles/Home.css";
import api from "../api";

export default function Home() {
  const navigate = useNavigate();
  const termsRef = useRef(null);
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeVenue, setActiveVenue] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [youtubeVideos, setYoutubeVideos] = useState([]);

  // Load CMS content
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/content/home.json", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          setContent(data);
        }
      } catch (e) {
        console.warn("Using default home content");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get("/youtube/recent");
        if (!cancelled) {
          setYoutubeVideos(Array.isArray(data?.videos) ? data.videos.slice(0, 3) : []);
        }
      } catch (e) {
        if (!cancelled) setYoutubeVideos([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Parallax scroll effects
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  // Mouse move effect for hero
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const scrollToNext = () => {
    const nextSection = document.querySelector("#venues");
    if (nextSection) nextSection.scrollIntoView({ behavior: "smooth" });
  };

  const [wishlist, setWishlist] = useState({});
  
  // Default venues (fallback)
  const defaultVenues = [
    {
      id: 1,
      name: "Royal Palace",
      location: "Jaipur",
      image: venue1,
      description: "A majestic palace with royal heritage charm for grand weddings.",
      tag: "Luxury · Trending",
      rating: 4.8,
      priceFrom: "₹2.5L/day",
      perks: ["Valet", "Fireworks", "Live Band"],
      guests: "200-500",
    },
    {
      id: 2,
      name: "Beach Resort",
      location: "Goa",
      image: venue2,
      description: "Say your vows with golden sands & ocean waves as your backdrop.",
      tag: "Romantic · Seaside",
      rating: 4.7,
      priceFrom: "₹1.9L/day",
      perks: ["Sea View", "Spa", "Sundeck"],
      guests: "100-300",
    },
    {
      id: 3,
      name: "Luxury Banquet",
      location: "Mumbai",
      image: venue3,
      description: "Modern elegance & world-class service in the heart of the city.",
      tag: "Modern · Elegant",
      rating: 4.6,
      priceFrom: "₹1.6L/day",
      perks: ["Valet", "Central AC", "LED Wall"],
      guests: "150-400",
    },
    {
      id: 4,
      name: "Heritage Haveli",
      location: "Udaipur",
      image: venue4,
      description: "Intimate courtyards, carved arches & lakeside sunsets—timeless.",
      tag: "Heritage · Intimate",
      rating: 4.9,
      priceFrom: "₹2.2L/day",
      perks: ["Lakeside", "Folk Dance", "Boat Ride"],
      guests: "50-150",
    },
  ];

  const venues = (content?.venues && content.venues.length > 0) ? content.venues : defaultVenues;
  const heroTitle = content?.hero?.title || "Discover Your Perfect destination Wedding Venue";
  const heroBadge = content?.hero?.badge || "India's Most Trusted Wedding Platform";
  const heroSubtitle = content?.hero?.subtitle || "500+ handpicked venues across 50+ destinations";
  const stats = content?.stats || { venues: 500, cities: 50, couples: 10000 };

  const toggleWishlist = (id) =>
    setWishlist((w) => ({ ...w, [id]: !w[id] }));

  const titleWords = heroTitle.split(" ");
  
  return (
    <div ref={containerRef} className="home-container">
      {/* Floating Terms button on the LEFT */}
      <TermsIconAdvanced ref={termsRef} trigger="fab" side="left" />

      {/* ================= ENHANCED HERO SECTION ================= */}
      <motion.section 
        className="home-wrapper"
        style={{ opacity, scale }}
      >
        <div className="video-container">
          <video
            className="bg-video"
            src="/15157499-sd_960_540_25fps.mp4"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="video-overlay-gradient" />
        </div>

        {/* Floating decorative elements */}
        <div className="floating-elements">
          <motion.div 
            className="floating-flower flower-1"
            animate={{ 
              y: [0, -20, 0], 
              rotate: [0, 5, 0],
              x: mousePosition.x * 0.5 
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="floating-flower flower-2"
            animate={{ 
              y: [0, 15, 0], 
              rotate: [0, -5, 0],
              x: mousePosition.x * -0.3 
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
        </div>

        <div className="hero-overlay">
          <div className="hero-center-content">
            {/* Logo - Premium feel preserved */}
            <motion.img
              src={logo}
              alt="Parivar Wedding Event Logo"
              className="hero-logo"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              style={{
                x: mousePosition.x * 0.2,
                y: mousePosition.y * 0.2
              }}
            />

            {/* Animated badge */}
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <span className="badge-star">✦</span>
              <span>{heroBadge}</span>
            </motion.div>

            {/* Animated title with word-by-word reveal */}
            <div className="hero-title-container">
              {titleWords.map((word, i) => (
                <motion.span
                  key={i}
                  className={`hero-title-word ${word === "Perfect" || word === "Venue" ? "highlight" : ""}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }}
                >
                  {word}
                </motion.span>
              ))}
            </div>

            {/* Animated subtitle */}
            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.8 }}
            >
              {heroSubtitle}
            </motion.p>

            {/* Stats row */}
            <motion.div 
              className="hero-stats-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.8 }}
            >
              <div className="hero-stat">
                <span className="stat-number">{stats.venues}+</span>
                <span className="stat-label">Venues</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="stat-number">{stats.cities}+</span>
                <span className="stat-label">Cities</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="stat-number">{(stats.couples / 1000).toFixed(0)}k+</span>
                <span className="stat-label">Happy Couples</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="hero-button-wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 0.8 }}
          >
            <motion.button
              className="hero-button"
              whileHover={{
                scale: 1.06,
                background: "linear-gradient(135deg, #ff9eb2, #ff6f91)",
                boxShadow: "0 12px 40px rgba(255,111,145,0.5)",
              }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToNext}
            >
              <span>Explore Venues</span>
              <motion.span 
                className="btn-arrow"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                →
              </motion.span>
            </motion.button>
            
            <motion.div 
              className="scroll-indicator"
              onClick={scrollToNext}
              whileHover={{ scale: 1.1 }}
            >
              <div className="mouse">
                <div className="wheel" />
              </div>
              <span>Scroll to explore</span>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* ================= DESTINATION WEDDINGS SECTION ================= */}
      <section className="destination-section">
        <div className="destination-container">
          <motion.div 
            className="destination-content"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="destination-text">
              <motion.span 
                className="destination-kicker"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Our Specialization
              </motion.span>
              
              <motion.h2 
                className="destination-title"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                Destination Weddings
              </motion.h2>
              
              <motion.p 
                className="destination-desc"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                From royal palaces in Rajasthan to beachfront resorts in Goa, 
                we curate extraordinary destination weddings that tell your unique love story. 
                Experience seamless planning, exclusive venues, and unforgettable moments.
              </motion.p>

              <motion.div 
                className="destination-features"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="feature-item">
                  <span className="feature-icon">✈️</span>
                  <span>End-to-end Planning</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🏰</span>
                  <span>Exclusive Venues</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🎨</span>
                  <span>Custom Themes</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">👨‍👩‍👧‍👦</span>
                  <span>Guest Management</span>
                </div>
              </motion.div>

              <motion.button
                className="destination-cta"
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/destination")}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                Plan Your Destination Wedding
                <span className="arrow">→</span>
              </motion.button>
            </div>

            <motion.div 
              className="destination-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <div className="destination-image-grid">
                <motion.div 
                  className="dest-img dest-img-1"
                  whileHover={{ scale: 1.05, zIndex: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src={venue1} alt="Palace Wedding" />
                  <div className="dest-img-overlay">
                    <span>Jaipur</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="dest-img dest-img-2"
                  whileHover={{ scale: 1.05, zIndex: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src={venue2} alt="Beach Wedding" />
                  <div className="dest-img-overlay">
                    <span>Goa</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="dest-img dest-img-3"
                  whileHover={{ scale: 1.05, zIndex: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src={venue4} alt="Lake Wedding" />
                  <div className="dest-img-overlay">
                    <span>Udaipur</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= ENHANCED VENUES SECTION ================= */}
      <section id="venues" className="venues-section">
        {/* Section header with parallax */}
        <motion.div 
          className="venues-head"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="kicker"
            initial={{ opacity: 0, letterSpacing: "0.5em" }}
            whileInView={{ opacity: 1, letterSpacing: "0.18em" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Curated for you
          </motion.p>
          <h2 className="title-serif">Featured Venues</h2>
          <motion.span 
            className="head-underline"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          <p className="section-desc">
            Handpicked destinations that promise unforgettable celebrations
          </p>
        </motion.div>

        {/* Category filter pills */}
        <motion.div 
          className="category-pills"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {["All", "Palace", "Beach", "Banquet", "Heritage", "Mountain"].map((cat, i) => (
            <motion.button
              key={cat}
              className={`category-pill ${cat === "All" ? "active" : ""}`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {/* ================= ENHANCED GRID ================= */}
        <div className="venues-grid-wrap">
          <img src={floralCorner} alt="" className="grid-floral" />
          
          <div className="venues-grid">
            {venues.map((v, i) => (
              <motion.article
                key={v.id}
                className="venue-card-v2"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                onMouseEnter={() => setActiveVenue(v.id)}
                onMouseLeave={() => setActiveVenue(null)}
              >
                {/* Image with advanced hover effects */}
                <div className="card-media-v2">
                  <motion.img 
                    src={v.image} 
                    alt={v.name} 
                    loading="lazy"
                    animate={{ 
                      scale: activeVenue === v.id ? 1.1 : 1,
                    }}
                    transition={{ duration: 0.6 }}
                  />
                  
                  {/* Gradient overlay on hover */}
                  <motion.div 
                    className="card-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: activeVenue === v.id ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="overlay-content">
                      <span className="guests-badge">
                        <span className="guests-icon">👥</span>
                        {v.guests} guests
                      </span>
                      <motion.button
                        className="view-details-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate(`/destination?id=${v.id}`)}
                      >
                        View Details
                      </motion.button>
                    </div>
                  </motion.div>

                  {/* Badges */}
                  <div className="card-badges-v2">
                    <span className="chip chip-theme">{v.tag}</span>
                    <span className="chip chip-price">{v.priceFrom}</span>
                  </div>

                  {/* Wishlist with animation */}
                  <motion.button
                    className={`wish-v2 ${wishlist[v.id] ? "active" : ""}`}
                    onClick={() => toggleWishlist(v.id)}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <AnimatePresence mode="wait">
                      {wishlist[v.id] ? (
                        <motion.svg 
                          key="filled"
                          viewBox="0 0 24 24" 
                          width="22" 
                          height="22"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#ff1744"/>
                        </motion.svg>
                      ) : (
                        <motion.svg 
                          key="outline"
                          viewBox="0 0 24 24" 
                          width="22" 
                          height="22"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" strokeWidth="2"/>
                        </motion.svg>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>

                {/* Enhanced card body */}
                <div className="card-body-v2">
                  <div className="card-header-row">
                    <div>
                      <h3 className="card-title-v2">{v.name}</h3>
                      <p className="card-loc-v2">
                        <span className="loc-icon">📍</span>
                        {v.location}
                      </p>
                    </div>
                    <div className="card-rating-badge">
                      <span className="rating-star">★</span>
                      <span>{v.rating}</span>
                    </div>
                  </div>

                  <p className="card-desc-v2">{v.description}</p>

                  {/* Perks as icon tags */}
                  <div className="card-perks-v2">
                    {v.perks.map((p) => (
                      <span key={p} className="perk-tag">
                        <span className="perk-dot">•</span>
                        {p}
                      </span>
                    ))}
                  </div>

                  {/* Action buttons */}
                  <div className="card-actions-v2">
                    <motion.button
                      className="btn-secondary"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/destination?id=${v.id}`)}
                    >
                      Details
                    </motion.button>
                    <motion.button
                      className="btn-primary-v2"
                      whileHover={{ 
                        scale: 1.02,
                        boxShadow: "0 8px 25px rgba(178,61,90,0.4)"
                      }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/destination")}
                    >
                      Book Now
                    </motion.button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* View all button */}
          <motion.div 
            className="view-all-wrap"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.button
              className="view-all-btn"
              whileHover={{ scale: 1.05, x: 5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/destination")}
            >
              <span>View All Venues</span>
              <span className="arrow">→</span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {youtubeVideos.length > 0 && (
        <section className="youtube-section">
          <div className="youtube-container">
            <motion.div
              className="venues-head youtube-head"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <motion.p
                className="kicker"
                initial={{ opacity: 0, letterSpacing: "0.5em" }}
                whileInView={{ opacity: 1, letterSpacing: "0.18em" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                
              </motion.p>
              <h2 className="title-serif">Recent YouTube Videos</h2>
              <motion.span
                className="head-underline"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
              <p className="section-desc">
                Watch our latest destination wedding stories and celebrations.
              </p>
            </motion.div>

            <div className="youtube-grid">
              {youtubeVideos.map((video, index) => (
                <motion.a
                  key={video.id}
                  className="youtube-card"
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <span className="youtube-thumb">
                    <img src={video.thumbnail} alt={video.title} loading="lazy" />
                    <span className="youtube-play">▶</span>
                  </span>
                  <span className="youtube-title">{video.title}</span>
                </motion.a>
              ))}
            </div>

            <div className="view-all-wrap youtube-view-all">
              <motion.a
                className="view-all-btn"
                href="https://www.youtube.com/@destinationwedding_01"
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Visit YouTube Channel</span>
                <span className="arrow">→</span>
              </motion.a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}